<script setup>
  import { ref, onMounted, getCurrentInstance } from 'vue';
  import ReviewSlider from './components/ReviewSlider.vue';
  import ReviewCard from './components/ReviewCard.vue';
  import ReviewCardSkeleton from './components/ReviewCardSkeleton.vue';

  import ProductReviews from 'assets/graphql/queries/ProductReviews.graphql';

  const props = defineProps({
    productId: {
      type: String,
      required: true,
    },
    productType: {
      type: String,
      required: true,
    },
    autoPlay: {
      type: Boolean,
      default: true,
    },
    autoPlayInterval: {
      type: Number,
      default: 6000,
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
  });

  const reviews = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const hasNextPage = ref(false);
  const sliderRef = ref(null);

  const instance = getCurrentInstance();
  const emit = defineEmits(['reviews-loaded']);

  const getHighlightedReviews = async () => {
    if (!props.productId || !props.productType) {
      error.value = 'Parámetros requeridos faltantes: productId o productType';
      reviews.value = [];
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await instance.proxy.$apollo.query({
        query: ProductReviews,
        variables: {
          product_id: props.productId,
          product_type: props.productType,
          page_number: 1,
          highlights: true,
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        context: {
          headers: {
            'x-hasura-role': 'anonymous',
          },
        },
      });

      if (!data) {
        throw new Error('No se recibieron datos de la consulta');
      }

      if (!data.paginated_product_surveys) {
        reviews.value = [];
        return;
      }

      const { results, page_info } = data.paginated_product_surveys;

      reviews.value = results || [];
      hasNextPage.value = page_info?.has_next || false;
      emit('reviews-loaded', reviews.value.length > 0);
    } catch (apolloError) {
      error.value =
        apolloError.message || 'Error desconocido al consultar reviews';
      reviews.value = [];
      emit('reviews-loaded', false);
    } finally {
      isLoading.value = false;
    }
  };

  const handleMouseEnter = () => {
    if (sliderRef.value?.stopAutoPlay) {
      sliderRef.value.stopAutoPlay();
    }
  };

  const handleMouseLeave = () => {
    if (sliderRef.value?.startAutoPlay) {
      sliderRef.value.startAutoPlay();
    }
  };

  onMounted(() => {
    getHighlightedReviews();
  });
</script>

<template>
  <div
    class="highlighted-reviews-slider"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="slider-content-wrapper">
      <div v-if="isLoading" class="loading-skeleton">
        <div class="skeleton-slider">
          <ReviewCardSkeleton />
        </div>
      </div>

      <template v-else>
        <ReviewSlider
          v-if="reviews.length > 0"
          ref="sliderRef"
          :items="reviews"
          :showNavigation="showNavigation"
          :autoPlay="autoPlay"
          :autoPlayInterval="autoPlayInterval"
        >
          <template #item="{ item: review }">
            <ReviewCard :review="review" />
          </template>
        </ReviewSlider>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '~assets/styles/index.scss';

  .highlighted-reviews-slider {
    position: relative;
    width: 100%;
    max-width: 650px;
    min-width: 0;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;

    @media (max-width: $screen-sm-max) {
      padding: 0 $XXSMALL;
      max-width: 100%;
    }

    @media (max-width: $screen-xs-max) {
      padding: 0 $XXSMALL;
      max-width: 100%;
    }
  }

  .slider-content-wrapper {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: $screen-sm-max) {
      padding: 0;
    }

    @media (max-width: $screen-xs-max) {
      padding: 0;
    }
  }

  .loading-skeleton {
    width: 100%;
    overflow: hidden;
  }

  .skeleton-slider {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: $screen-xs-max) {
      max-width: 343px;
    }
  }

  .no-reviews {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
    color: #999;
    font-style: italic;
  }
</style>
